import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { RotateCcw } from 'lucide-react'
import { AdminShell, adminPage } from '#/components/admin/admin-shell'
import { DataTable, Pager, SearchBox, StatTile } from '#/components/admin/ui'
import { UserDrawer } from '#/components/admin/user-drawer'
import { OwnerCreateUserButton } from '#/components/admin/user-plan-owner'
import { Providers, SubBadge, UserAvatar, isOnline } from '#/components/admin/user-bits'
import { Badge } from '#/components/ui/badge'
import { Button } from '#/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '#/components/ui/select'
import { useAdminUsers, useAdminUserStats, type AdminUser } from '#/lib/api-admin'
import { fmtCurrency, fmtDate, fmtDateTime, fmtNumber, fmtRelative } from '#/lib/format'
import { m } from '#/paraglide/messages'

export const Route = createFileRoute('/admin/users')({ component: adminPage(Page) })

const ALL = 'all'
type Filters = { role: string; provider: string; status: string; active: string; sort: string }
const DEFAULT: Filters = { role: ALL, provider: ALL, status: ALL, active: ALL, sort: 'created' }
const opt = (v: string) => (v === ALL ? undefined : v)

function Page() {
  const [q, setQ] = useState('')
  const [page, setPage] = useState(1)
  const [f, setF] = useState<Filters>(DEFAULT)
  const [open, setOpen] = useState<string | null>(null)
  const set = (k: keyof Filters) => (v: string) => { setF((p) => ({ ...p, [k]: v })); setPage(1) }
  const { data, isLoading } = useAdminUsers({ q, page, role: opt(f.role), provider: opt(f.provider), status: opt(f.status), active: opt(f.active), sort: f.sort })
  const { data: st } = useAdminUserStats()
  const dirty = q || JSON.stringify(f) !== JSON.stringify(DEFAULT)
  return (
    <AdminShell wide title={m.admin_nav_users()} description={m.admin_users_desc()} actions={<div className="flex items-center gap-2"><OwnerCreateUserButton /><SearchBox value={q} onChange={(v) => { setQ(v); setPage(1) }} placeholder={m.admin_users_search()} /></div>}>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        <StatTile label={m.admin_users_stat_total()} value={st ? fmtNumber(st.total) : '—'} sub={st ? m.admin_users_stat_new({ n: fmtNumber(st.new7d), m: fmtNumber(st.new30d) }) : undefined} />
        <StatTile label={m.admin_users_stat_active1d()} value={st ? fmtNumber(st.active1d) : '—'} sub={st ? m.admin_users_stat_active_sub({ n: fmtNumber(st.active7d), m: fmtNumber(st.active30d) }) : undefined} />
        <StatTile label={m.admin_users_stat_paid()} value={st ? fmtNumber(st.paid) : '—'} sub={st ? m.admin_users_stat_paid_sub({ n: fmtNumber(st.total - st.paid) }) : undefined} />
        <StatTile label={m.admin_users_stat_google()} value={st ? fmtNumber(st.google) : '—'} sub={st ? m.admin_users_stat_google_sub({ n: fmtNumber(st.credential), m: fmtNumber(st.admins) }) : undefined} />
        <StatTile label={m.admin_users_stat_plans()} value={st ? fmtNumber(st.withPlans) : '—'} sub={st ? m.admin_users_stat_plans_sub({ n: fmtNumber(st.withTokens) }) : undefined} />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Filter label={m.admin_users_filter_role()} value={f.role} onChange={set('role')} items={[[ALL, m.admin_users_all()], ['admin', m.admin_users_role_admin()], ['user', m.admin_users_role_user()]]} />
        <Filter label={m.admin_users_filter_provider()} value={f.provider} onChange={set('provider')} items={[[ALL, m.admin_users_all()], ['google', m.admin_users_provider_google()], ['credential', m.admin_users_provider_credential()]]} />
        <Filter label={m.admin_users_filter_status()} value={f.status} onChange={set('status')} items={[[ALL, m.admin_users_all()], ['paid', m.admin_users_status_paid()], ['free', m.admin_users_status_free()], ['cancelled', m.admin_users_status_cancelled()]]} />
        <Filter label={m.admin_users_filter_active()} value={f.active} onChange={set('active')} items={[[ALL, m.admin_users_all()], ['1d', m.admin_users_active_1d()], ['7d', m.admin_users_active_7d()], ['30d', m.admin_users_active_30d()]]} />
        <Filter label={m.admin_users_filter_sort()} value={f.sort} onChange={set('sort')} items={[['created', m.admin_users_sort_created()], ['seen', m.admin_users_sort_seen()], ['login', m.admin_users_sort_login()], ['plans', m.admin_users_sort_plans()], ['paid', m.admin_users_sort_paid()]]} />
        {dirty ? (
          <Button variant="ghost" size="sm" onClick={() => { setQ(''); setF(DEFAULT); setPage(1) }}>
            <RotateCcw className="size-3.5" /> {m.admin_users_reset()}
          </Button>
        ) : null}
      </div>

      <DataTable<AdminUser>
        rows={data?.users}
        loading={isLoading}
        rowKey={(u) => u.id}
        onRow={(u) => setOpen(u.id)}
        cols={[
          { key: 'user', header: m.admin_users_col_user(), render: (u) => <UserCell u={u} /> },
          { key: 'signin', header: m.admin_users_col_signin(), render: (u) => <Providers providers={u.providers} /> },
          { key: 'sub', header: m.admin_users_col_sub(), render: (u) => <SubCell u={u} /> },
          { key: 'seen', header: m.admin_users_col_seen(), render: (u) => <When at={u.lastSeenAt} /> },
          { key: 'login', header: m.admin_users_col_login(), render: (u) => <When at={u.lastLoginAt} /> },
          { key: 'usage', header: m.admin_users_col_usage(), render: (u) => <div className="text-xs tabular-nums">{m.admin_users_usage_line({ plans: u.plansCount, prd: u.prdUsed, chat: u.chatUsed })}{u.lastPlanTitle ? <div className="mt-0.5 max-w-48 truncate text-muted-foreground" title={u.lastPlanTitle}>{u.lastPlanTitle}</div> : null}</div> },
          { key: 'cli', header: m.admin_users_col_cli(), render: (u) => <CliCell u={u} /> },
          { key: 'paid', header: m.admin_users_col_paid(), render: (u) => <PaidCell u={u} />, className: 'text-right' },
          { key: 'created', header: m.admin_users_col_created(), render: (u) => <span className="text-xs text-muted-foreground" title={fmtDateTime(u.createdAt)}>{fmtDate(u.createdAt)}</span> },
        ]}
      />
      {data ? <Pager page={data.page} total={data.total} pageSize={data.pageSize} onPage={setPage} /> : null}
      <UserDrawer userId={open} onClose={() => setOpen(null)} />
    </AdminShell>
  )
}

function Filter({ label, value, onChange, items }: { label: string; value: string; onChange: (v: string) => void; items: [string, string][] }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger size="sm" className="h-8 gap-1.5 text-xs" aria-label={label}>
        <span className="text-muted-foreground">{label}:</span>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {items.map(([v, l]) => (
          <SelectItem key={v} value={v}>{l}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}


function UserCell({ u }: { u: AdminUser }) {
  const online = isOnline(u)
  return (
    <div className="flex items-center gap-2.5">
      <div className="relative shrink-0">
        <UserAvatar u={u} className="size-8" />
        {online ? <span className="absolute -right-0.5 -bottom-0.5 size-2.5 rounded-full border-2 border-card bg-status-done" title={m.admin_users_online()} /> : null}
      </div>
      <div className="min-w-0">
        <div className="flex items-center gap-1.5">
          <span className="truncate font-medium">{u.name || u.email}</span>
          {u.role === 'admin' ? <Badge className="h-4 px-1.5 text-[10px]">{m.admin_users_role_admin()}</Badge> : null}
        </div>
        <div className="truncate text-xs text-muted-foreground">{u.email}</div>
      </div>
    </div>
  )
}



function SubCell({ u }: { u: AdminUser }) {
  return (
    <div className="space-y-0.5">
      <SubBadge sub={u.subscription} />
      {u.subscription ? <div className="text-[11px] text-muted-foreground">{m.admin_user_sub_until({ date: fmtDate(u.subscription.expiresAt) })}</div> : null}
    </div>
  )
}

function When({ at }: { at: string | null }) {
  if (!at) return <span className="text-xs text-muted-foreground">{m.admin_users_never()}</span>
  return (
    <div className="text-xs tabular-nums" title={fmtDateTime(at)}>
      <div>{fmtRelative(at)}</div>
      <div className="text-muted-foreground">{fmtDate(at)}</div>
    </div>
  )
}

function CliCell({ u }: { u: AdminUser }) {
  if (!u.tokensCount) return <span className="text-xs text-muted-foreground">{m.admin_users_cli_none()}</span>
  return (
    <div className="text-xs tabular-nums">
      <div>{m.admin_users_cli_tokens({ n: u.tokensCount })}</div>
      <div className="text-muted-foreground">{u.tokenLastUsedAt ? m.admin_users_cli_used({ when: fmtRelative(u.tokenLastUsedAt) }) : m.admin_user_token_never()}</div>
    </div>
  )
}

function PaidCell({ u }: { u: AdminUser }) {
  if (!u.paidIdr && !u.paidUsd) return <span className="text-xs text-muted-foreground">—</span>
  return (
    <div className="text-xs tabular-nums">
      {u.paidIdr ? <div>{fmtCurrency(u.paidIdr, 'IDR')}</div> : null}
      {u.paidUsd ? <div>{fmtCurrency(u.paidUsd, 'USD')}</div> : null}
    </div>
  )
}
